import { Course } from '../courses/types';

export const SELENIUM_COURSES: Course[] = [
  {
    id: 'selenium-fundamentals',
    title: 'Test Automation with Selenium',
    description: 'Master Selenium WebDriver for automated testing across browsers and frameworks',
    level: 'Intermediate',
    duration: '24 hours',
    totalDuration: '24 hours',
    imageSrc: '/api/placeholder/400/300',
    requirements: ['Basic programming knowledge', 'Understanding of web applications'],
    learningOutcomes: [
      'Master Selenium WebDriver fundamentals',
      'Implement Page Object Model pattern',
      'Handle dynamic web elements effectively',
      'Set up cross-browser testing',
      'Integrate with CI/CD pipelines'
    ],
    modules: [
      {
        id: 'selenium-intro',
        title: 'Introduction to Selenium',
        description: 'Overview of Selenium suite and WebDriver architecture',
        content: `## What is Selenium?

Selenium is a powerful open-source framework for automating web browsers. It provides a suite of tools for testing web applications across different browsers and platforms.

### Selenium Components:
- **Selenium WebDriver**: Direct communication with browsers
- **Selenium IDE**: Record and playback tool
- **Selenium Grid**: Distributed testing across multiple machines

### Why Selenium?
- Cross-browser compatibility
- Language flexibility (Java, C#, Python, JavaScript)
- Large community support
- Integration with testing frameworks`,
        duration: '3 hours',
        difficulty: 'beginner',
        order: 1
      },
      {
        id: 'selenium-setup',
        title: 'Setting Up Selenium Environment',
        description: 'Install and configure Selenium WebDriver',
        content: `## Environment Setup

Learn to set up Selenium WebDriver for different programming languages and browsers.

### Prerequisites:
- Java/Python/C# development environment
- Browser drivers (ChromeDriver, GeckoDriver)
- IDE setup and dependencies

### Installation Steps:
1. Download and install browser drivers
2. Set up project dependencies
3. Configure WebDriver paths
4. Write your first test script`,
        duration: '2 hours',
        difficulty: 'beginner',
        order: 2
      },
      {
        id: 'selenium-locators',
        title: 'Element Locators and Strategies',
        description: 'Master different locator strategies for finding web elements',
        content: `## Element Locators

Understanding how to locate elements is crucial for effective automation.

### Locator Types:
- **ID**: Most reliable when available
- **Name**: Good for form elements
- **Class Name**: Use with caution for uniqueness
- **Tag Name**: For generic element types
- **Link Text**: For link elements
- **Partial Link Text**: For partial matches
- **CSS Selector**: Powerful and flexible
- **XPath**: Most versatile but can be fragile

### Best Practices:
- Prioritize stable locators
- Avoid using absolute XPath
- Use data attributes when possible`,
        duration: '4 hours',
        difficulty: 'intermediate',
        order: 3
      },
      {
        id: 'selenium-interactions',
        title: 'Web Element Interactions',
        description: 'Learn to interact with various web elements',
        content: `## Element Interactions

Master different ways to interact with web elements effectively.

### Common Interactions:
- Click operations
- Text input and clearing
- Dropdown selections
- Checkbox and radio buttons
- File uploads
- Mouse hover actions
- Keyboard shortcuts

### Advanced Interactions:
- JavaScript executor
- Action chains
- Window and frame handling`,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 4
      },
      {
        id: 'selenium-waits',
        title: 'Waits and Synchronization',
        description: 'Handle timing issues with explicit and implicit waits',
        content: `## Synchronization Strategies

Learn to handle timing issues in dynamic web applications.

### Types of Waits:
- **Implicit Wait**: Global timeout for element finding
- **Explicit Wait**: Wait for specific conditions
- **Fluent Wait**: Customizable polling and ignore exceptions

### Wait Conditions:
- Element to be clickable
- Element to be visible
- Text to be present
- Page title contains
- Custom conditions

### Best Practices:
- Avoid Thread.sleep()
- Use explicit waits for dynamic content
- Set reasonable timeout values`,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 5
      },
      {
        id: 'selenium-pom',
        title: 'Page Object Model Pattern',
        description: 'Implement maintainable test automation architecture',
        content: `## Page Object Model (POM)

Learn the industry-standard pattern for organizing automation code.

### POM Benefits:
- Code reusability
- Easy maintenance
- Reduced code duplication
- Better readability

### Implementation:
- Create page classes
- Separate locators from test logic
- Use page factory pattern
- Implement base page class

### Best Practices:
- One page object per web page
- Return page objects from actions
- Use meaningful method names`,
        duration: '4 hours',
        difficulty: 'intermediate',
        order: 6
      },
      {
        id: 'selenium-frameworks',
        title: 'Testing Frameworks Integration',
        description: 'Integrate Selenium with TestNG, JUnit, and other frameworks',
        content: `## Testing Framework Integration

Learn to integrate Selenium with popular testing frameworks.

### Framework Options:
- **TestNG**: Feature-rich testing framework
- **JUnit**: Simple and widely used
- **NUnit**: .NET testing framework
- **pytest**: Python testing framework

### Features:
- Test annotations
- Test grouping and categorization
- Parallel execution
- Data-driven testing
- Reporting and logging

### Configuration:
- XML configuration files
- Test suite organization
- Cross-browser testing setup`,
        duration: '3 hours',
        difficulty: 'advanced',
        order: 7
      },
      {
        id: 'selenium-cicd',
        title: 'CI/CD Integration and Best Practices',
        description: 'Integrate automated tests into continuous integration pipelines',
        content: `## CI/CD Integration

Learn to integrate Selenium tests into your development workflow.

### CI/CD Platforms:
- Jenkins
- GitHub Actions
- Azure DevOps
- GitLab CI

### Implementation:
- Dockerized test execution
- Headless browser configuration
- Test result reporting
- Screenshot capture on failures

### Best Practices:
- Fast feedback loops
- Stable test suite
- Proper error handling
- Test data management`,
        duration: '2 hours',
        difficulty: 'advanced',
        order: 8
      }
    ],
    type: 'devops'
  },

  {
    id: 'selenium-csharp',
    title: 'Selenium with C#',
    description: 'Master Selenium automation using C#, NUnit, and .NET ecosystem',
    level: 'Intermediate',
    duration: '20 hours',
    totalDuration: '20 hours',
    imageSrc: '/api/placeholder/400/300',
    requirements: ['C# programming knowledge', 'Basic understanding of web applications'],
    learningOutcomes: [
      'Build Selenium automation with C#',
      'Use NUnit testing framework',
      'Implement Page Object Model in C#',
      'Handle advanced scenarios',
      'Create maintainable test suites'
    ],
    modules: [
      {
        id: 'selenium-csharp-setup',
        title: 'C# Selenium Setup',
        description: 'Set up Selenium WebDriver with C# and Visual Studio',
        content: `## C# Selenium Environment Setup

Configure your development environment for C# Selenium automation.

### Required Tools:
- Visual Studio or VS Code
- .NET Framework/.NET Core
- NuGet Package Manager
- Browser drivers

### NuGet Packages:
- Selenium.WebDriver
- Selenium.Support
- NUnit or MSTest
- WebDriverManager (optional)

### Project Structure:
- Test project setup
- Configuration management
- Driver initialization`,
        duration: '2 hours',
        difficulty: 'beginner',
        order: 1
      },
      {
        id: 'selenium-csharp-basics',
        title: 'C# WebDriver Fundamentals',
        description: 'Learn basic WebDriver operations in C#',
        content: `## C# WebDriver Basics

Master fundamental WebDriver operations using C# syntax.

### Driver Initialization:
\`\`\`csharp
IWebDriver driver = new ChromeDriver();
driver.Navigate().GoToUrl("https://example.com");
\`\`\`

### Element Operations:
- FindElement() and FindElements()
- Click(), SendKeys(), Clear()
- GetAttribute(), Text property
- IsDisplayed, IsEnabled properties

### C# Specific Features:
- LINQ for element collections
- Exception handling with try-catch
- Using statements for resource management`,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 2
      },
      {
        id: 'selenium-csharp-nunit',
        title: 'NUnit Integration',
        description: 'Integrate Selenium tests with NUnit framework',
        content: `## NUnit Testing Framework

Learn to structure and run Selenium tests with NUnit.

### NUnit Attributes:
- [Test] - Mark test methods
- [SetUp] / [TearDown] - Setup and cleanup
- [TestFixture] - Test class marking
- [TestCase] - Parameterized tests

### Test Organization:
- Test class structure
- Test categorization
- Parallel execution
- Data-driven testing

### Assertions:
- Assert.That() syntax
- Custom assertion messages
- Exception assertions`,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 3
      },
      {
        id: 'selenium-csharp-pom',
        title: 'Page Object Model in C#',
        description: 'Implement POM pattern using C# best practices',
        content: `## C# Page Object Model

Create maintainable automation architecture with C# OOP principles.

### C# POM Implementation:
- Properties for web elements
- Methods for page actions
- Constructor for driver initialization
- Inheritance for common functionality

### Design Patterns:
- Factory pattern for page creation
- Builder pattern for complex objects
- Singleton for driver management

### Example Structure:
\`\`\`csharp
public class LoginPage : BasePage
{
    private IWebElement UsernameField => driver.FindElement(By.Id("username"));
    private IWebElement PasswordField => driver.FindElement(By.Id("password"));
    
    public HomePage Login(string username, string password)
    {
        UsernameField.SendKeys(username);
        PasswordField.SendKeys(password);
        SubmitButton.Click();
        return new HomePage(driver);
    }
}
\`\`\``,
        duration: '4 hours',
        difficulty: 'intermediate',
        order: 4
      },
      {
        id: 'selenium-csharp-advanced',
        title: 'Advanced C# Selenium Techniques',
        description: 'Master advanced automation scenarios with C#',
        content: `## Advanced C# Selenium Techniques

Handle complex automation scenarios using advanced C# features.

### Advanced Topics:
- JavaScript execution
- File downloads and uploads
- Multiple window handling
- Frame switching
- Alert handling

### C# Specific Features:
- Async/await for better performance
- Extension methods for WebDriver
- Generic methods for reusability
- LINQ for data manipulation

### Error Handling:
- Custom exception classes
- Retry mechanisms
- Logging integration
- Screenshot capture`,
        duration: '4 hours',
        difficulty: 'advanced',
        order: 5
      },
      {
        id: 'selenium-csharp-reporting',
        title: 'Test Reporting and Logging',
        description: 'Implement comprehensive reporting for C# Selenium tests',
        content: `## Test Reporting in C#

Create detailed test reports and logging for better test management.

### Reporting Tools:
- ExtentReports for C#
- Allure reporting
- NUnit HTML reports
- Custom reporting solutions

### Logging Frameworks:
- NLog
- Serilog
- log4net
- Built-in logging

### Implementation:
- Screenshot on failure
- Test step reporting
- Performance metrics
- Test data logging`,
        duration: '2 hours',
        difficulty: 'intermediate',
        order: 6
      },
      {
        id: 'selenium-csharp-cicd',
        title: 'C# Selenium in CI/CD',
        description: 'Deploy C# Selenium tests in continuous integration',
        content: `## CI/CD with C# Selenium

Integrate your C# Selenium tests into CI/CD pipelines.

### Build Tools:
- MSBuild
- dotnet CLI
- Azure DevOps
- GitHub Actions

### Configuration:
- appsettings.json for environments
- Command line parameters
- Environment variables
- Docker containerization

### Best Practices:
- Headless execution
- Parallel test execution
- Test categorization
- Artifact management`,
        duration: '2 hours',
        difficulty: 'advanced',
        order: 7
      }
    ],
    type: 'devops'
  }
];